/* The block renderer: the registry, and the two functions that walk it.
   ------------------------------------------------------------------------
   ITEM 3 OF docs/plan/00-prestavba-2026-08.md, finished.

   A document is a list of blocks and this is the table that turns each one into
   markup. The keys are the contract — they must keep matching `Block["k"]` in
   lib/types.ts, and the JSON Schema in content/schema/ says the same thing to
   whoever is writing the material.

   **The components live beside this file, grouped by what they are, not one
   file per kind.** The plan asked for sixteen files; thirteen of them would
   have been four lines of markup under a header comment three times that long,
   which tells a reader less than one page of related kinds does. What earns a
   file of its own is behaviour: the exercise, the cards, the held-back question
   and the file maker each have one.

   The look is settled and lives in styles/base.css (the page), deck.css (the
   projector) and print.css (paper). Three surfaces, one markup — which is why a
   block never styles itself and every kind writes `data-k`, so a stylesheet can
   say what it wants about the kind it is drawing.
   ------------------------------------------------------------------------ */

import type { ReactNode } from "react";

import { AppExample } from "./AppFrame";
import { Ask } from "./Ask";
import { Cards } from "./Cards";
import { Exercise } from "./Exercise";
import { Maker } from "./Maker";
import { Table } from "./Table";
import { Callout, Cut, Figure, Heading, Points, Quotes, Rule, Sig, Text } from "./prose";
import { Compare, Example, Prompt, Steps } from "./prompts";
import { revealed, useReveal } from "../routes/deck/reveal";
import type { Block, BlockKind } from "../lib/types";

/* `T` is the material's localized string and half the components below are
   written in terms of it; a page that renders a fragment of content of its own
   reaches for it here rather than reimplementing the fallback mark. */
export { T } from "./shared";

/* Keyed by BlockKind, not by string. With `string` a kind added to the union
   and forgotten here compiled cleanly and rendered nothing at all — which on a
   projector reads as missing content rather than as a mistake. Now it is a
   compile error, and check-portal.mjs holds the schema to the same list. */
const RENDERERS: Record<BlockKind, (b: never) => ReactNode> = {
  text:     (b: Extract<Block, { k: "text" }>)     => <Text b={b} />,
  rule:     ()                                     => <Rule />,
  cut:      (b: Extract<Block, { k: "cut" }>)      => <Cut b={b} />,
  h:        (b: Extract<Block, { k: "h" }>)        => <Heading b={b} />,
  points:   (b: Extract<Block, { k: "points" }>)   => <Points b={b} />,
  steps:    (b: Extract<Block, { k: "steps" }>)    => <Steps b={b} />,
  prompt:   (b: Extract<Block, { k: "prompt" }>)   => <Prompt b={b} />,
  example:  (b: Extract<Block, { k: "example" }>)  => <Example b={b} />,
  compare:  (b: Extract<Block, { k: "compare" }>)  => <Compare b={b} />,
  table:    (b: Extract<Block, { k: "table" }>)    => <Table b={b} />,
  callout:  (b: Extract<Block, { k: "callout" }>)  => <Callout b={b} />,
  exercise: (b: Extract<Block, { k: "exercise" }>) => <Exercise b={b} />,
  cards:    (b: Extract<Block, { k: "cards" }>)    => <Cards b={b} />,
  quotes:   (b: Extract<Block, { k: "quotes" }>)   => <Quotes b={b} />,
  sig:      (b: Extract<Block, { k: "sig" }>)      => <Sig b={b} />,
  figure:   (b: Extract<Block, { k: "figure" }>)   => <Figure b={b} />,
  maker:    (b: Extract<Block, { k: "maker" }>)    => <Maker block={b} />,
  ask:      (b: Extract<Block, { k: "ask" }>)      => <Ask b={b} />,
  app:      (b: Extract<Block, { k: "app" }>)      => <AppExample b={b} />,
};

export function BlockView({ block }: { block: Block }) {
  const at = useReveal();
  const render = RENDERERS[block.k];
  /* An unknown kind is a content bug, not a crash: the rest of the page still
     has to render, and the hole has to be visible to whoever wrote the JSON. */
  if (!render) return <div className="block-unknown">?{block.k}</div>;
  return (
    <div className={"block" + (revealed(block.step, at) ? " on" : "")}
         data-k={block.k} data-step={block.step}>
      {render(block as never)}
    </div>
  );
}

export function Blocks({ blocks }: { blocks: Block[] }) {
  return <>{blocks.map((b, i) => <BlockView key={i} block={b} />)}</>;
}
